/**
 * Session state store.
 * Manages the respondent's session lifecycle.
 */

import { create } from 'zustand';
import type {
  Session,
  SessionProgress,
  SessionEndSummary,
  SessionOutcome,
  TerminalOutcome,
} from '@/types';
import {
  startSession,
  updateProgress,
  setFingerprint,
  completeSession,
  terminateSession,
  telemetryService,
} from '@/lib/api';

interface SessionState {
  // Session data
  sessionId: string | null;
  session: Session | null;

  // Status flags
  isActive: boolean;
  isStarted: boolean;
  isEnded: boolean;

  // Timing
  startedAt: Date | null;
  endedAt: Date | null;

  // End summary
  endSummary: SessionEndSummary | null;

  // Terminal signal pushed by the BE on a submit (overquota/timeout/etc).
  terminal: TerminalOutcome | null;

  // Actions
  initSession: (sessionId: string) => Promise<void>;
  start: () => Promise<void>;
  startSession: (surveyId: string) => Promise<void>;
  updateSessionProgress: (progress: Partial<SessionProgress>) => Promise<void>;
  setSessionFingerprint: (fingerprint: string) => Promise<void>;
  complete: () => Promise<SessionEndSummary>;
  completeSession: () => Promise<SessionEndSummary>;
  terminate: (
    outcome: SessionOutcome,
    reason?: string
  ) => Promise<SessionEndSummary>;
  terminateSession: (
    outcome: SessionOutcome,
    reason?: string
  ) => Promise<SessionEndSummary>;
  applyTerminal: (terminal: TerminalOutcome) => void;
  reset: () => void;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  // Initial state
  sessionId: null,
  session: null,
  isActive: false,
  isStarted: false,
  isEnded: false,
  startedAt: null,
  endedAt: null,
  endSummary: null,
  terminal: null,

  // Initialize session (set session ID, prepare for start). Clears any prior
  // session's end state — otherwise a stale terminal/end-summary from a
  // previous sid would keep SurveyPage on the end screen for the new session.
  initSession: async (sessionId: string) => {
    set({
      sessionId,
      session: null,
      isActive: false,
      isStarted: false,
      isEnded: false,
      startedAt: null,
      endedAt: null,
      endSummary: null,
      terminal: null,
    });
  },

  // Start the session (call API to mark as started)
  start: async () => {
    const { sessionId } = get();
    if (!sessionId) {
      throw new Error('Session ID not set');
    }

    const session = await startSession(sessionId);

    // Initialize telemetry for this session
    telemetryService.init(
      sessionId,
      session.surveyVersionId || session.surveyId,
    );

    set({
      session,
      isActive: session.status === 'active',
      isStarted: true,
      startedAt: new Date(),
    });
  },

  // Start session with survey ID (alias for initSession + start)
  startSession: async (_surveyId: string) => {
    // Note: surveyId is used to generate a session, but for the player
    // the sessionId should already be set via URL
    const { sessionId } = get();
    if (!sessionId) {
      throw new Error('Session ID not set. Use initSession first.');
    }

    const session = await startSession(sessionId);

    set({
      session,
      isActive: session.status === 'active',
      isStarted: true,
      startedAt: new Date(),
    });
  },

  // Update progress
  updateSessionProgress: async (progress: Partial<SessionProgress>) => {
    const { sessionId } = get();
    if (!sessionId) return;

    const updatedSession = await updateProgress(sessionId, progress);

    set((state) => ({
      session: state.session
        ? { ...state.session, progress: updatedSession.progress }
        : null,
    }));
  },

  // Set fingerprint
  setSessionFingerprint: async (fingerprint: string) => {
    const { sessionId } = get();
    if (!sessionId) return;

    await setFingerprint(sessionId, fingerprint);
  },

  // Complete the session
  complete: async () => {
    const { sessionId } = get();
    if (!sessionId) {
      throw new Error('Session ID not set');
    }

    // Track completion in telemetry
    telemetryService.trackSessionCompleted();

    const summary = await completeSession(sessionId);

    // Stop telemetry
    await telemetryService.stop();

    set({
      isActive: false,
      isEnded: true,
      endedAt: new Date(),
      endSummary: summary,
      session: get().session
        ? { ...get().session!, status: 'completed', outcome: 'complete' }
        : null,
    });

    return summary;
  },

  // Alias for complete
  completeSession: async () => {
    return get().complete();
  },

  // Terminate the session
  terminate: async (outcome: SessionOutcome, reason?: string) => {
    const { sessionId } = get();
    if (!sessionId) {
      throw new Error('Session ID not set');
    }

    // Track termination in telemetry
    telemetryService.trackSessionTerminated(reason || outcome);

    const summary = await terminateSession(sessionId, outcome, reason);

    // Stop telemetry
    await telemetryService.stop();

    set({
      isActive: false,
      isEnded: true,
      endedAt: new Date(),
      endSummary: summary,
      session: get().session
        ? { ...get().session!, status: 'terminated', outcome }
        : null,
    });

    return summary;
  },

  // Alias for terminate
  terminateSession: async (outcome: SessionOutcome, reason?: string) => {
    return get().terminate(outcome, reason);
  },

  // Apply a BE-pushed terminal (the session was already finalized server-side,
  // so we don't call terminate/complete again — just end locally and record
  // the signal so SurveyPage can render the redirect screen).
  applyTerminal: (terminal: TerminalOutcome) => {
    set({
      isActive: false,
      isEnded: true,
      endedAt: new Date(),
      terminal,
    });
  },

  // Reset store
  reset: () => {
    set({
      sessionId: null,
      session: null,
      isActive: false,
      isStarted: false,
      isEnded: false,
      startedAt: null,
      endedAt: null,
      endSummary: null,
      terminal: null,
    });
  },
}));
